import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { 
      path: '/', 
      label: language === 'fr' ? 'Accueil' : language === 'en' ? 'Home' : 'الرئيسية'
    },
    { 
      path: '/voyages', 
      label: language === 'fr' ? 'Agence de Voyage' : language === 'en' ? 'Travel Agency' : 'وكالة السفر'
    },
    { 
      path: '/it-services', 
      label: language === 'fr' ? 'Solutions IT' : language === 'en' ? 'IT Solutions' : 'حلول تقنية المعلومات'
    },
    { 
      path: '/about', 
      label: language === 'fr' ? 'À propos' : language === 'en' ? 'About' : 'من نحن'
    },
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50" data-testid="main-header">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Connected Horizon */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img
              src="/logo.png"
              alt="Connected Horizon"
              className="h-14 w-auto object-contain"
            />
            <div className="hidden sm:flex flex-col">
              <span className="text-lg font-bold text-maquette-slate leading-none">CONNECTED</span>
              <span className="text-sm font-medium text-maquette-copper leading-none">HORIZON</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-maquette-blue font-semibold'
                    : 'text-maquette-text hover:text-maquette-blue'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <LanguageSelector />
            <Link
              to="/contact"
              className="bg-maquette-dark text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-maquette-blue transition-colors shadow-sm"
              data-testid="cta-demander-devis"
            >
              {language === 'fr' ? 'Demander un devis' : language === 'en' ? 'Request a quote' : 'طلب عرض أسعار'}
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageSelector />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-maquette-blue/10 text-maquette-blue font-semibold'
                      : 'text-maquette-text hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className="bg-maquette-dark text-white px-4 py-2 rounded-lg text-center font-medium hover:bg-maquette-blue transition-colors"
              >
                {language === 'fr' ? 'Demander un devis' : language === 'en' ? 'Request a quote' : 'طلب عرض أسعار'}
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;